import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  FileText, Clock, AlertTriangle, DollarSign,
  ArrowUpRight, Users, ClipboardList, TrendingUp, CheckCircle2, XCircle
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const CHART_COLORS = ['#18181B', '#3B82F6', '#52525B', '#A1A1AA', '#D4D4D8', '#F59E0B', '#10B981', '#EF4444'];
const STATUS_COLOR_MAP = {
  DRAFT: '#A1A1AA', PENDING: '#F59E0B', PENDING_APPROVAL: '#F59E0B',
  ORDER: '#10B981', UNAPPROVED: '#6366F1', REJECTED: '#EF4444',
  CANCELLED: '#64748B', RFQ: '#3B82F6', DORMANT: '#D4D4D8', TEMPLATE: '#52525B',
};

const fmt$ = (v) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
};
const fmtFull = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

export default function RequisitionDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/requisitions/dashboard/summary')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load requisitions dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const drillTo = (params) => {
    const sp = new URLSearchParams({ tab: 'list', ...params });
    navigate(`/requests?${sp.toString()}`);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin h-6 w-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full" />
    </div>
  );
  if (!data) return <div className="text-center py-16 text-zinc-400">Dashboard unavailable</div>;

  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="requisition-dashboard">
      <KPIStrip kpis={k} drillTo={drillTo} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ApprovalRisks aging={data.approval_aging} highValue={data.high_value_pending} navigate={navigate} drillTo={drillTo} />
        </div>
        <StatusBreakdown data={data.by_status} drillTo={drillTo} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SubmissionTrend data={data.trend} />
        <ByOriginator data={data.by_originator} drillTo={drillTo} />
      </div>
      <ByDepartment data={data.by_department} drillTo={drillTo} />
    </div>
  );
}

/* ────────────────────── KPI STRIP ────────────────────── */
function KPIStrip({ kpis: k, drillTo }) {
  const cards = [
    { label: 'Total PRs', value: k.total_count, icon: ClipboardList, testid: 'kpi-total-prs',
      onClick: () => drillTo({ label: 'All Requisitions' }) },
    { label: 'Total Value', value: fmt$(k.total_value), icon: DollarSign, testid: 'kpi-total-value',
      onClick: () => drillTo({ label: 'All Requisitions' }) },
    { label: 'MTD Submissions', value: k.mtd_count, icon: TrendingUp, testid: 'kpi-mtd',
      sub: fmt$(k.mtd_value), subColor: 'text-zinc-500',
      onClick: () => drillTo({ label: 'This Month' }) },
    { label: 'Pending Approval', value: k.pending_count, icon: Clock, warn: k.pending_count > 0, testid: 'kpi-pending',
      onClick: () => drillTo({ status: 'PENDING,PENDING_APPROVAL,UNAPPROVED', label: 'Pending Approval' }) },
    { label: 'Approved (PO)', value: k.approved_count, icon: CheckCircle2, testid: 'kpi-approved',
      onClick: () => drillTo({ status: 'ORDER', label: 'Approved / PO Created' }) },
    { label: 'Rejected', value: k.rejected_count, icon: XCircle, warn: k.rejected_count > 0, testid: 'kpi-rejected',
      onClick: () => drillTo({ status: 'REJECTED', label: 'Rejected' }) },
  ];

  return (
    <div className="flex flex-col md:flex-row flex-wrap lg:flex-nowrap border border-zinc-200 rounded-md overflow-hidden shadow-sm" data-testid="req-kpi-strip">
      {cards.map((c, i) => {
        const Icon = c.icon;
        return (
          <div key={i} onClick={c.onClick}
            className={`flex-1 min-w-[140px] p-4 cursor-pointer transition-all hover:bg-zinc-50 active:bg-zinc-100 group ${i > 0 ? 'border-t md:border-t-0 md:border-l' : ''} border-zinc-200 bg-white`}
            data-testid={c.testid}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <Icon className={`h-3.5 w-3.5 ${c.warn ? 'text-red-500' : 'text-zinc-400'}`} strokeWidth={1.5} />
                <span className="text-[11px] font-semibold tracking-wide uppercase text-zinc-500">{c.label}</span>
              </div>
              <ArrowUpRight className="h-3 w-3 text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <p className={`text-xl font-semibold tracking-tight ${c.warn ? 'text-red-600' : 'text-zinc-900'}`}>{c.value}</p>
            {c.sub && <p className={`text-[11px] mt-0.5 ${c.subColor || 'text-zinc-400'}`}>{c.sub}</p>}
          </div>
        );
      })}
    </div>
  );
}

/* ────────────────────── APPROVAL RISKS ────────────────────── */
function ApprovalRisks({ aging, highValue, navigate, drillTo }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="approval-risks">
      <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-500" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-zinc-900">Approval Pipeline & Risks</h3>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">Pending Approvals by Age</p>
          <div className="flex gap-3">
            {[
              { label: '0-3 days', count: aging["0_3"], color: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' },
              { label: '4-7 days', count: aging["4_7"], color: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' },
              { label: '7+ days', count: aging["7_plus"], color: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' },
            ].map(b => (
              <div key={b.label}
                onClick={() => drillTo({ status: 'PENDING,PENDING_APPROVAL,UNAPPROVED', label: `Approvals ${b.label}` })}
                className={`flex-1 border rounded-md p-3 text-center cursor-pointer transition-colors ${b.color}`}
                data-testid={`req-aging-${b.label.replace(/\s/g, '-')}`}>
                <p className="text-lg font-bold">{b.count}</p>
                <p className="text-[10px] font-medium uppercase tracking-wide">{b.label}</p>
              </div>
            ))}
          </div>
        </div>
        {highValue.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1">
              High-Value Pending
              <Badge className="bg-amber-100 text-amber-700 text-[9px] ml-1">{highValue.length}</Badge>
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {highValue.slice(0, 6).map(r => (
                <div key={r.request_number}
                  className="flex items-center justify-between text-xs px-3 py-2 rounded hover:bg-zinc-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/requests/${r.request_number}`)}
                  data-testid={`high-value-req-${r.request_number}`}>
                  <span className="font-mono text-primary font-semibold">{r.request_number}</span>
                  <span className="text-zinc-500 truncate mx-2">{r.originator}</span>
                  <span className="font-semibold whitespace-nowrap">{fmtFull(r.value)}</span>
                  <Badge className="bg-zinc-100 text-zinc-600 text-[9px] ml-2">{r.age_days}d</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────────────── STATUS BREAKDOWN (Donut) ────────────────────── */
function StatusBreakdown({ data, drillTo }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  const chartData = data.map(d => ({ ...d, fill: STATUS_COLOR_MAP[d.status] || '#D4D4D8' }));

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="req-status-breakdown">
      <div className="px-5 py-3.5 border-b border-zinc-200">
        <h3 className="text-sm font-semibold text-zinc-900">PR Status Breakdown</h3>
      </div>
      <div className="p-5 flex flex-col items-center">
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie data={chartData} dataKey="count" nameKey="status" cx="50%" cy="50%"
              innerRadius={50} outerRadius={75} strokeWidth={2} stroke="#fff"
              onClick={(entry) => entry && drillTo({ status: entry.status, label: `Status: ${entry.status}` })}
              cursor="pointer">
              {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </Pie>
            <Tooltip formatter={(val, name) => [`${val} PRs`, name]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="text-center -mt-4 mb-3">
          <p className="text-2xl font-bold text-zinc-900">{total}</p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Total PRs</p>
        </div>
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
          {chartData.map(d => (
            <div key={d.status}
              className="flex items-center gap-1.5 text-xs cursor-pointer hover:opacity-70 transition-opacity"
              onClick={() => drillTo({ status: d.status, label: `Status: ${d.status}` })}
              data-testid={`req-status-${d.status}`}>
              <div className="h-2.5 w-2.5 rounded-full" style={{ background: d.fill }} />
              <span className="text-zinc-600">{d.status.replace(/_/g, ' ')}</span>
              <span className="font-semibold text-zinc-900">{d.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────── SUBMISSION TREND (Line) ────────────────────── */
function SubmissionTrend({ data }) {
  const chartData = data.map(d => ({ ...d, label: d.month.slice(5) }));
  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="req-trend">
      <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900">Submission Trend</h3>
        <span className="text-[10px] text-zinc-400 uppercase tracking-wide">Last 12 months</span>
      </div>
      <div className="p-5">
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip formatter={(v, name) => [name === 'count' ? `${v} PRs` : fmtFull(v), name === 'count' ? 'Count' : 'Value']} />
            <Line type="monotone" dataKey="count" stroke="#18181B" strokeWidth={2} dot={{ r: 3, fill: '#18181B' }} />
            <Line type="monotone" dataKey="value" stroke="#3B82F6" strokeWidth={1.5} strokeDasharray="5 5" dot={false} yAxisId="right" />
            <YAxis yAxisId="right" orientation="right" tickFormatter={v => fmt$(v)} tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ────────────────────── BY ORIGINATOR (Bar) ────────────────────── */
function ByOriginator({ data, drillTo }) {
  const chartData = data.map(d => ({
    ...d,
    name: d.originator.length > 16 ? d.originator.slice(0, 14) + '...' : d.originator,
    fullName: d.originator
  }));

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="req-by-originator">
      <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center gap-2">
        <Users className="h-4 w-4 text-zinc-400" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-zinc-900">Top Requestors</h3>
      </div>
      <div className="p-5">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
            <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#52525b' }} width={110} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v, name) => [name === 'count' ? `${v} PRs` : fmtFull(v), name === 'count' ? 'PRs' : 'Value']} />
            <Bar dataKey="count" fill="#18181B" radius={[0, 4, 4, 0]} barSize={16}
              onClick={(entry) => entry && entry.fullName && drillTo({ search: entry.fullName, label: `Requestor: ${entry.fullName}` })}
              cursor="pointer" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ────────────────────── BY DEPARTMENT (Horizontal Bar) ────────────────────── */
function ByDepartment({ data, drillTo }) {
  const chartData = data.map((d, i) => ({
    ...d,
    name: d.department,
    fill: CHART_COLORS[i % CHART_COLORS.length]
  }));

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="req-by-department">
      <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center gap-2">
        <FileText className="h-4 w-4 text-zinc-400" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-zinc-900">Spend by Department</h3>
      </div>
      <div className="p-5">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={v => fmt$(v)} tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => [fmtFull(v), 'Value']} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={28}
              onClick={(entry) => entry && drillTo({ search: entry.name, label: `Dept: ${entry.name}` })}
              cursor="pointer">
              {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
